import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Input from "../../../../../components/Input";
import Dropdown from "../../../../../components/Dropdown";
import Switch from "../../../../../components/Switch";
import { VALIDATION_FIELD_MAPPING } from "../../../../../utils/appConstant";
import { getStartEndOfMonth } from "../../../../../utils/methods";
import { ListFilters } from "../../../../../interfaces/payout.interface";
import useDebounce from "../../../../../hooks/useDebounce";

interface FilterProps {
  // Row 1
  properties: any[];
  allProperties: any[];
  managers: any[];
  allManagers: any[];
  platforms: any[];
  allPlatforms: any[];

  // Row 2
  bankAccounts: any[];
  allBankAccounts: any[];
  payoutTypes: any[];
  allPayoutTypes: any[];
  payoutStatuses: any[];
  allPayoutStatuses: any[];
  reservationStatus: any[];
  allReservationStatus: any[];

  // Row 3
  actions: any[];
  allActions: any[];
  payoutManagedBy: any[];
  allPayoutManagedBy: any[];

  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  initialFilters: ListFilters;
  onApply: (filters: ListFilters) => void;
  onReset: () => void;
}

const Filter: React.FC<FilterProps> = ({
  properties, allProperties,
  managers, allManagers,
  platforms, allPlatforms,
  bankAccounts, allBankAccounts,
  payoutTypes, allPayoutTypes,
  payoutStatuses, allPayoutStatuses,
  reservationStatus, allReservationStatus,
  actions, allActions,
  payoutManagedBy, allPayoutManagedBy,
  dropdownSearchData, onDropdownSearchChange,
  initialFilters, onApply, onReset,
}) => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<ListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);

  const defaultDates = useMemo(() => getStartEndOfMonth(), []);

  // Calculate initial filtersApplied state
  const isApplied =
    initialFilters.searchText !== '' ||
    initialFilters.property !== '' ||
    initialFilters.managedBy !== '' ||
    initialFilters.platform !== '' ||
    initialFilters.bankAccount !== '' ||
    initialFilters.payoutType !== '' ||
    initialFilters.payoutStatus !== '' ||
    initialFilters.reservationStatus !== '' ||
    initialFilters.reservationStatus !== '' ||
    initialFilters.action !== '' ||
    initialFilters.payoutManagedBy !== '' ||
    !!initialFilters.checkoutOnly ||
    initialFilters.startDate !== defaultDates.startDate ||
    initialFilters.endDate !== defaultDates.endDate;

  const [filtersApplied, setFiltersApplied] = useState(isApplied);

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Effect to handle real-time application
  useEffect(() => {
    const filtersToApply = {
      ...filters,
      searchText: debouncedSearchText
    };

    const applied =
      filtersToApply.searchText !== '' ||
      filtersToApply.property !== '' ||
      filtersToApply.managedBy !== '' ||
      filtersToApply.platform !== '' ||
      filtersToApply.bankAccount !== '' ||
      filtersToApply.payoutType !== '' ||
      filtersToApply.payoutStatus !== '' ||
      filtersToApply.reservationStatus !== '' ||
      filtersToApply.reservationStatus !== '' ||
      filtersToApply.action !== '' ||
      filtersToApply.payoutManagedBy !== '' ||
      !!filtersToApply.checkoutOnly ||
      filtersToApply.startDate !== defaultDates.startDate ||
      filtersToApply.endDate !== defaultDates.endDate;

    setFiltersApplied(applied);
    onApply(filtersToApply);
  }, [
    debouncedSearchText,
    filters.property, filters.managedBy, filters.platform,
    filters.bankAccount, filters.payoutType, filters.payoutStatus,
    filters.reservationStatus, filters.action, filters.payoutManagedBy, filters.checkoutOnly,
    filters.startDate, filters.endDate,
    defaultDates, onApply,
  ]);

  const handleInputChange = useCallback((event: any, name: string) => {
    setFilters((prev: ListFilters) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleSwitchChange = useCallback((event: any, name: string) => {
    setFilters((prev: ListFilters) => ({ ...prev, [name]: event.target.checked }));
  }, []);

  const handleReset = () => {
    const defaults: ListFilters = {
      searchText: '',
      property: '',
      managedBy: '',
      platform: '',
      bankAccount: '',
      payoutType: '',
      payoutStatus: '',
      reservationStatus: '',
      action: '',
      payoutManagedBy: '',
      checkoutOnly: false,
      ...getStartEndOfMonth(),
    };
    setFilters(defaults);
    onReset();
  };

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`}>
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t("common.filters")}</h5>
          {filtersApplied && (
            <small
              onClick={handleReset}
              className="cursor-pointer text-primary"
            >
              {t("common.clearFilters")}
            </small>
          )}
        </div>

        <div className="col-12">
          {/* Row 1: Start Date, End Date, Property, Managed By, Platform */}
          <div className="row g-3 mb-3">
            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.startDate}
                name={VALIDATION_FIELD_MAPPING.startDate}
                max={filters.endDate}
                placeholder={t('reconciliation.payout.list.pageTexts.filters.startDate')}
                value={filters.startDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.endDate}
                name={VALIDATION_FIELD_MAPPING.endDate}
                min={filters.startDate || undefined}
                placeholder={t('reconciliation.payout.list.pageTexts.filters.endDate')}
                value={filters.endDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.property ? [filters.property] : []}
                options={(properties || []).map((property: any) => ({
                  label: property.name,
                  value: property.id
                }))}
                allOptions={(allProperties || []).map((property: any) => ({
                  label: property.name,
                  value: property.id
                }))}
                id={VALIDATION_FIELD_MAPPING.property}
                name={VALIDATION_FIELD_MAPPING.property}
                searchText={dropdownSearchData.property}
                onSearch={onDropdownSearchChange}
                placeholder={t("reconciliation.payout.list.pageTexts.filters.selectProperty")}
                label={t("reconciliation.payout.list.pageTexts.filters.property")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.managedBy ? [filters.managedBy] : []}
                options={(managers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id
                }))}
                allOptions={(allManagers || []).map((manager: any) => ({
                  label: manager.name,
                  value: manager.id
                }))}
                id={VALIDATION_FIELD_MAPPING.managedBy}
                name={VALIDATION_FIELD_MAPPING.managedBy}
                searchText={dropdownSearchData.managedBy}
                onSearch={onDropdownSearchChange}
                placeholder={t("reconciliation.payout.list.pageTexts.filters.selectManagedBy")}
                label={t("reconciliation.payout.list.pageTexts.filters.managedBy")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-2 col-sm-4">
              <Dropdown
                values={filters.platform ? [filters.platform] : []}
                options={(platforms || []).map((platform: any) => ({
                  label: platform.name,
                  value: platform.id
                }))}
                allOptions={(allPlatforms || []).map((platform: any) => ({
                  label: platform.name,
                  value: platform.id
                }))}
                id={VALIDATION_FIELD_MAPPING.platform}
                name={VALIDATION_FIELD_MAPPING.platform}
                searchText={dropdownSearchData.platform}
                onSearch={onDropdownSearchChange}
                placeholder={t("reconciliation.payout.list.pageTexts.filters.selectPlatform")}
                label={t("reconciliation.payout.list.pageTexts.filters.platform")}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Row 2: Bank Account, Payout Type, Payout Status, Reservation Status, Invoice Status */}
          <div className="row g-3 mb-3">
            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.bankAccount ? [filters.bankAccount] : []}
                options={(bankAccounts || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allBankAccounts || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.bankAccount}
                name={VALIDATION_FIELD_MAPPING.bankAccount}
                searchText={dropdownSearchData.bankAccount}
                onSearch={onDropdownSearchChange}
                placeholder={t("reconciliation.payout.list.pageTexts.filters.selectBankAccount")}
                label={t("reconciliation.payout.list.pageTexts.filters.bankAccount")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.payoutType ? [filters.payoutType] : []}
                options={(payoutTypes || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allPayoutTypes || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.payoutType}
                name={VALIDATION_FIELD_MAPPING.payoutType}
                searchText={dropdownSearchData.payoutType}
                onSearch={onDropdownSearchChange}
                placeholder={t("reconciliation.payout.list.pageTexts.filters.selectPayoutType")}
                label={t("reconciliation.payout.list.pageTexts.filters.payoutType")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.payoutStatus ? [filters.payoutStatus] : []}
                options={(payoutStatuses || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allPayoutStatuses || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.payoutStatus}
                name={VALIDATION_FIELD_MAPPING.payoutStatus}
                searchText={dropdownSearchData.payoutStatus}
                onSearch={onDropdownSearchChange}
                placeholder={t("reconciliation.payout.list.pageTexts.filters.selectPayoutStatus")}
                label={t("reconciliation.payout.list.pageTexts.filters.payoutStatus")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-6">
              <Dropdown
                values={filters.reservationStatus ? [filters.reservationStatus] : []}
                options={(reservationStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allReservationStatus || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.reservationStatus}
                name={VALIDATION_FIELD_MAPPING.reservationStatus}
                searchText={dropdownSearchData.reservationStatus}
                onSearch={onDropdownSearchChange}
                placeholder={t("reconciliation.payout.list.pageTexts.filters.selectReservationStatus")}
                label={t("reconciliation.payout.list.pageTexts.filters.reservationStatus")}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Row 3: Action Dropdown, Search Box */}
          <div className="row g-3">
            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.action ? [filters.action] : []}
                options={(actions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id === '' ? '__blank__' : item.id
                }))}
                allOptions={(allActions || []).map((item: any) => ({
                  label: item.name,
                  value: item.id === '' ? '__blank__' : item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.action}
                name={VALIDATION_FIELD_MAPPING.action}
                searchText={dropdownSearchData.action}
                onSearch={onDropdownSearchChange}
                placeholder={t("reconciliation.payout.list.pageTexts.filters.selectAction")}
                label={t("reconciliation.payout.list.pageTexts.filters.action")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-3 col-sm-4">
              <Dropdown
                values={filters.payoutManagedBy ? [filters.payoutManagedBy] : []}
                options={(payoutManagedBy || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                allOptions={(allPayoutManagedBy || []).map((item: any) => ({
                  label: item.name,
                  value: item.id
                }))}
                id={VALIDATION_FIELD_MAPPING.payoutManagedBy}
                name={VALIDATION_FIELD_MAPPING.payoutManagedBy}
                searchText={dropdownSearchData.payoutManagedBy}
                onSearch={onDropdownSearchChange}
                placeholder={t("reconciliation.payout.list.pageTexts.filters.selectPayoutManagedBy")}
                label={t("reconciliation.payout.list.pageTexts.filters.payoutManagedBy")}
                onChange={handleInputChange}
              />
            </div>

            <div className="col-md-4 col-sm-12">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name={VALIDATION_FIELD_MAPPING.searchText}
                placeholder={t("reconciliation.payout.list.pageTexts.filters.searchPlaceholder")}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>

            <div className="col-md-2 col-sm-4 d-flex align-items-middle pb-1">
              <Switch
                name="checkoutOnly"
                label={t("reconciliation.payout.list.pageTexts.filters.checkoutOnly")}
                checked={!!filters.checkoutOnly}
                onChange={handleSwitchChange}
                offColorGrey={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filter;
